// Skills (docs/12 "Workflow capabilities", D62): the library of instructions workflows attach;
// each is a folder of files with SKILL.md at its root, written into the workspace before every
// step. Searchable (the URL's `q`); creating one opens its editor. Archive, never delete.
import { BookOpenText, PencilSimple, Plus } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { ActionIcon, Button, Tooltip } from "rizzui";

import { usePermissions } from "../auth/permissions";
import { SearchBox } from "../catalog/SearchBox";
import { useUrlSearch } from "../catalog/useUrlSearch";
import { type Messages, useT } from "../i18n";
import { sectionCrumbs, SECTIONS } from "../layout/nav";
import { toast } from "../layout/toast";
import { createSkill, type Skill, type SkillMeta, skillsQuery } from "../skills/api";
import { SkillForm } from "../skills/SkillForm";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { type Column, DataTable } from "../ui/DataTable";
import { PageHeader } from "../ui/PageHeader";
import { SideDrawer } from "../ui/SideDrawer";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { Mono, Pill } from "../ui/status";

const columns = ({ skills: t, common }: Messages): Column<Skill>[] => [
  {
    key: "name",
    header: t.columns.name,
    cell: (s) => (
      <div className="min-w-0">
        <Link to={`/skills/${s.id}`} className="truncate font-semibold text-gray-900 hover:text-primary">
          {s.name}
        </Link>
        <Mono className="block text-xs text-gray-500">{s.slug}</Mono>
      </div>
    ),
  },
  { key: "description", header: t.columns.description, hideBelow: "lg", cell: (s) => <span className="line-clamp-2 text-gray-600">{s.description || "—"}</span> },
  { key: "files", header: t.columns.files, hideBelow: "sm", cell: (s) => <span className="tabular">{s.file_count}</span> },
  {
    key: "used",
    header: t.columns.usedBy,
    hideBelow: "md",
    cell: (s) => <span className="tabular">{s.used_by_versions === 0 ? "—" : t.versions(s.used_by_versions)}</span>,
  },
  { key: "status", header: t.columns.status, cell: (s) => (s.status === "active" ? <Pill tone="green">{t.active}</Pill> : <Pill tone="gray">{t.archived}</Pill>) },
  {
    key: "actions",
    header: "",
    className: "text-end",
    cell: (s) => (
      <Tooltip content={common.actions.open} placement="top">
        <Link to={`/skills/${s.id}`} aria-label={t.open(s.name)}>
          <ActionIcon as="span" size="sm" variant="outline">
            <PencilSimple weight="bold" className="h-4 w-4" />
          </ActionIcon>
        </Link>
      </Tooltip>
    ),
  },
];

export default function SkillsPage() {
  const messages = useT();
  const t = messages.skills;
  const crumbs = sectionCrumbs("skills");
  const search = useUrlSearch();
  const skills = useQuery({ ...skillsQuery(search.q), placeholderData: (previous) => previous });
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  async function create(meta: SkillMeta) {
    const created = await createSkill(meta);
    await queryClient.invalidateQueries({ queryKey: ["skills"] });
    toast(t.created(created.skill.name));
    void navigate(`/skills/${created.skill.id}`);
  }

  return (
    <>
      <PageHeader title={SECTIONS.skills.title} breadcrumb={crumbs} description={SECTIONS.skills.subtitle}>
        {can("ManageSkills") ? (
          <Button className="gap-1.5" onClick={() => setCreating(true)}>
            <Plus weight="bold" className="h-4 w-4" /> {t.new}
          </Button>
        ) : null}
      </PageHeader>
      <SearchBox className="mb-4 md:w-80" label={t.search} placeholder={t.searchPlaceholder} text={search.text} onChange={search.setText} />
      <div className={cn(cardClass, "p-3 lg:p-4")}>
        {skills.isPending ? <Loading /> : null}
        {skills.isError ? <ErrorState title={t.loadError} message={skills.error.message} /> : null}
        {skills.data?.length === 0 ? (
          <EmptyState compact icon={<BookOpenText weight="duotone" />} title={search.q ? t.nothingMatches : t.empty}>
            {search.q ? t.noMatch(search.q) : t.emptyHelp}
          </EmptyState>
        ) : null}
        {skills.data && skills.data.length > 0 ? <DataTable label={t.title} columns={columns(messages)} rows={skills.data} rowKey={(s) => s.id} /> : null}
      </div>
      <SideDrawer open={creating} onClose={() => setCreating(false)} title={t.new} subtitle={t.newSubtitle}>
        {creating ? <SkillForm submitLabel={t.create} onSubmit={create} onCancel={() => setCreating(false)} /> : null}
      </SideDrawer>
    </>
  );
}
